import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Search, X, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YMAPS_API_KEY = process.env.REACT_APP_YMAPS_API_KEY || "f5c65dae-0af2-455e-ab75-7d68b708b987";

// Load Yandex Maps JS API once (idempotent)
let ymapsPromise = null;
function loadYmaps() {
  if (window.ymaps) return Promise.resolve(window.ymaps);
  if (ymapsPromise) return ymapsPromise;
  ymapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://api-maps.yandex.ru/2.1/?apikey=${YMAPS_API_KEY}&lang=ru_RU`;
    s.async = true;
    s.onload = () => window.ymaps.ready(() => resolve(window.ymaps));
    s.onerror = () => reject(new Error("Failed to load Yandex Maps"));
    document.head.appendChild(s);
  });
  return ymapsPromise;
}

const formatWorkTime = (s) => (s ? s.replace(/\\n/g, " · ") : "");

export default function CdekPvzPicker({ city, onSelect, selectedCode }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const objectsCollectionRef = useRef(null);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);  // CDEK API down/unconfigured
  const [search, setSearch] = useState("");
  const [activeOffice, setActiveOffice] = useState(null);
  const [manualPvz, setManualPvz] = useState({ address: "", code: "" });
  const [mapReady, setMapReady] = useState(false);

  // Fetch PVZ list whenever city changes
  useEffect(() => {
    if (!city || city.length < 2) {
      setOffices([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setUnavailable(false);
    fetch(`${API_URL}/api/cdek/pvz?city=${encodeURIComponent(city)}`)
      .then(async (r) => {
        if (r.status === 503) {
          if (!cancelled) setUnavailable(true);
          return null;
        }
        if (!r.ok) throw new Error("fetch");
        return r.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        setOffices(data.offices || []);
        if ((data.offices || []).length === 0) {
          setError(`Не найдено пунктов выдачи в "${city}"`);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Не удалось загрузить пункты выдачи");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [city]);

  // Init map once
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    let cancelled = false;
    loadYmaps()
      .then((ymaps) => {
        if (cancelled || !mapDivRef.current) return;
        const map = new ymaps.Map(mapDivRef.current, {
          center: [55.751244, 37.618423], // Moscow default
          zoom: 10,
          controls: ["zoomControl"],
        }, {
          suppressMapOpenBlock: true,
          yandexMapDisablePoiInteractivity: true,
        });
        // Disable scroll zoom so page can scroll past the map
        map.behaviors.disable("scrollZoom");
        mapRef.current = map;
        objectsCollectionRef.current = new ymaps.GeoObjectCollection();
        map.geoObjects.add(objectsCollectionRef.current);
        setMapReady(true);
      })
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, []);

  // Re-render markers when offices change
  useEffect(() => {
    const map = mapRef.current;
    const coll = objectsCollectionRef.current;
    if (!map || !coll || !window.ymaps) return;
    coll.removeAll();
    if (offices.length === 0) return;

    const ymaps = window.ymaps;
    offices.forEach((o) => {
      if (o.lat == null || o.lng == null) return;
      const placemark = new ymaps.Placemark(
        [o.lat, o.lng],
        {
          balloonContentHeader: `<b>${o.name || "СДЭК"}</b>`,
          balloonContentBody: `
            <div style="font-size:12px;line-height:1.5">
              <div>${o.addressFull || o.address || ""}</div>
              ${o.workTime ? `<div style="color:#666;margin-top:4px">${formatWorkTime(o.workTime)}</div>` : ""}
              ${o.phone ? `<div style="color:#666">📞 ${o.phone}</div>` : ""}
            </div>
          `,
          hintContent: o.address || o.name,
          officeCode: o.code,
        },
        {
          preset: selectedCode === o.code ? "islands#redIcon" : "islands#greenDotIcon",
          balloonCloseButton: true,
        }
      );
      placemark.events.add("click", () => setActiveOffice(o));
      coll.add(placemark);
    });

    if (offices.length > 0 && offices[0].lat != null) {
      try {
        const bounds = coll.getBounds();
        if (bounds) map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 30 });
      } catch {
        map.setCenter([offices[0].lat, offices[0].lng], 11);
      }
    }
  }, [offices, selectedCode, mapReady]);

  // Filter offices by search text
  const filtered = search.trim()
    ? offices.filter((o) =>
        (o.address || "").toLowerCase().includes(search.trim().toLowerCase()) ||
        (o.name || "").toLowerCase().includes(search.trim().toLowerCase())
      )
    : offices;

  const handleSelect = useCallback((o) => {
    setActiveOffice(null);
    onSelect && onSelect(o);
  }, [onSelect]);

  // Manual PVZ entry fallback (when CDEK API unavailable)
  if (unavailable) {
    return (
      <div className="w-full p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="pvz-manual">
        <p className="text-[12px] font-sans text-amber-800 mb-3">
          ⚠️ Карта СДЭК временно недоступна. Введите код или адрес выбранного ПВЗ вручную:
        </p>
        <div className="space-y-2">
          <input
            value={manualPvz.code}
            onChange={(e) => setManualPvz((m) => ({ ...m, code: e.target.value }))}
            placeholder="Код ПВЗ (напр. MSK1234)"
            className="w-full bg-white rounded-lg px-3 py-2 text-[13px] font-sans focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            data-testid="pvz-manual-code"
          />
          <input
            value={manualPvz.address}
            onChange={(e) => setManualPvz((m) => ({ ...m, address: e.target.value }))}
            placeholder="Адрес ПВЗ"
            className="w-full bg-white rounded-lg px-3 py-2 text-[13px] font-sans focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            data-testid="pvz-manual-address"
          />
          <button
            type="button"
            onClick={() => {
              if (!manualPvz.code.trim() || !manualPvz.address.trim()) return;
              onSelect && onSelect({
                code: manualPvz.code.trim(),
                address: manualPvz.address.trim(),
                addressFull: manualPvz.address.trim(),
                city,
              });
            }}
            disabled={!manualPvz.code.trim() || !manualPvz.address.trim()}
            className="w-full bg-amber-700 text-white font-sans font-medium py-2.5 rounded-lg text-[13px] disabled:opacity-50"
            data-testid="pvz-manual-confirm"
          >
            Подтвердить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="cdek-pvz-picker">
      {/* Search */}
      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40" strokeWidth={1.6} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Поиск по адресу${offices.length ? ` (${offices.length} ПВЗ)` : ""}`}
          className="w-full bg-[#F5F0EB] rounded-xl pl-9 pr-3 py-2.5 text-[13px] font-sans text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/15"
          data-testid="pvz-search"
          disabled={!offices.length}
        />
      </div>

      {/* Map */}
      <div className="relative rounded-xl overflow-hidden bg-[#E8E3DE]" style={{ height: 240 }}>
        <div ref={mapDivRef} className="w-full h-full" data-testid="pvz-map" />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-10" data-testid="pvz-loading">
            <div className="w-6 h-6 border-2 border-[#1A1A1A]/15 border-t-[#1A1A1A] rounded-full animate-spin" />
          </div>
        )}
        {!loading && error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/85 backdrop-blur-sm p-5 text-center z-10">
            <p className="text-[12px] font-sans text-[#1A1A1A]/70">{error}</p>
          </div>
        )}
      </div>

      {/* Office list (scrollable, scrolls map into view) */}
      {filtered.length > 0 && (
        <div className="mt-2 max-h-[180px] overflow-y-auto bg-white rounded-xl divide-y divide-[#1A1A1A]/8" data-testid="pvz-list">
          {filtered.slice(0, 50).map((o) => {
            const selected = selectedCode === o.code;
            return (
              <button
                key={o.code}
                type="button"
                onClick={() => handleSelect(o)}
                className={`w-full px-3 py-2.5 flex items-start gap-2 text-left active:bg-[#F5F0EB] transition-colors ${selected ? "bg-[#1A1A1A]/5" : ""}`}
                data-testid={`pvz-item-${o.code}`}
              >
                <MapPin size={14} className="text-[#1A1A1A]/55 mt-0.5 shrink-0" strokeWidth={1.6} />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-sans font-medium text-[#1A1A1A] truncate">
                    {o.address || o.name}
                  </p>
                  {o.workTime && (
                    <p className="text-[10px] font-sans text-[#1A1A1A]/55 truncate">
                      {formatWorkTime(o.workTime)}
                    </p>
                  )}
                </div>
                {selected && <Check size={14} className="text-emerald-600 mt-0.5" strokeWidth={2.5} />}
              </button>
            );
          })}
        </div>
      )}

      {/* Active office detail (from map click) */}
      {activeOffice && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-4 bg-white rounded-t-2xl shadow-2xl border-t border-[#1A1A1A]/10" data-testid="pvz-active-modal">
          <button
            onClick={() => setActiveOffice(null)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#F5F0EB] flex items-center justify-center"
          >
            <X size={16} strokeWidth={1.8} />
          </button>
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#1A1A1A]/55 font-serif">CDEK · {activeOffice.code}</p>
          <p className="text-[14px] font-sans font-medium text-[#1A1A1A] mt-1">{activeOffice.address || activeOffice.name}</p>
          {activeOffice.workTime && (
            <p className="text-[12px] font-sans text-[#1A1A1A]/65 mt-1">{formatWorkTime(activeOffice.workTime)}</p>
          )}
          {activeOffice.phone && (
            <p className="text-[12px] font-sans text-[#1A1A1A]/55 mt-0.5">📞 {activeOffice.phone}</p>
          )}
          <button
            onClick={() => handleSelect(activeOffice)}
            className="mt-3 w-full bg-[#1A1A1A] text-white font-sans font-medium py-3 rounded-full text-[13px]"
            data-testid="pvz-confirm-active"
          >
            Выбрать этот ПВЗ
          </button>
        </div>
      )}
    </div>
  );
}
